import { useState, useRef, useEffect } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  PaperPlaneRight, Sparkle, User, X, Minus, Trash, Microphone, SpeakerHigh, Stop, ArrowsOut, ArrowsIn
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check Web Speech API support
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const speechSynthesis = window.speechSynthesis;

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speakingIndex, setSpeakingIndex] = useState(null);
  const [analyzingIndex, setAnalyzingIndex] = useState(null);
  const [lastInputWasVoice, setLastInputWasVoice] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Listen for open chatbot event from Dashboard
  useEffect(() => {
    const handleOpenChatbot = () => {
      setIsOpen(true);
      setIsMinimized(false);
    };
    window.addEventListener('openChatbot', handleOpenChatbot);
    return () => window.removeEventListener('openChatbot', handleOpenChatbot);
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (isOpen && !isMinimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [history, isOpen, isMinimized]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  // Load chat history from localStorage
  useEffect(() => {
    const savedHistory = localStorage.getItem('gresart_chatbot_history');
    if (savedHistory) {
      try {
        setHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Error loading chat history:", e);
      }
    }
  }, []);

  // Save chat history to localStorage
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem('gresart_chatbot_history', JSON.stringify(history.slice(-30)));
    }
  }, [history]);

  // Auto-speak response when last input was voice
  useEffect(() => {
    if (lastInputWasVoice && history.length > 0 && !loading) {
      const lastMessage = history[history.length - 1];
      if (lastMessage.role === 'assistant') {
        speakText(lastMessage.content || lastMessage.summary || '', history.length - 1);
        setLastInputWasVoice(false);
      }
    }
  }, [history, loading, lastInputWasVoice]);

  // Initialize speech recognition
  useEffect(() => {
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'pt-PT';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setMessage(prev => prev + transcript);
        setIsListening(false);
        setLastInputWasVoice(true);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microfone não permitido');
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      speechSynthesis?.cancel();
    };
  }, []);

  // Toggle voice input
  const toggleListening = () => {
    if (!SpeechRecognition) {
      toast.error('O seu navegador não suporta reconhecimento de voz');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current?.start();
        setIsListening(true);
      } catch (error) {
        console.error('Error starting recognition:', error);
        toast.error('Erro ao iniciar microfone');
      }
    }
  };

  // Speak text using TTS
  const speakText = (text, index) => {
    if (!speechSynthesis) {
      toast.error('O seu navegador não suporta síntese de voz');
      return;
    }

    // Stop if already speaking this message
    if (isSpeaking && speakingIndex === index) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      setSpeakingIndex(null);
      return;
    }

    // Cancel any ongoing speech
    speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pt-PT';
    utterance.rate = 0.9;
    utterance.pitch = 1;

    // Try to find Portuguese voice
    const voices = speechSynthesis.getVoices();
    const ptVoice = voices.find(v => v.lang.startsWith('pt'));
    if (ptVoice) {
      utterance.voice = ptVoice;
    }

    utterance.onstart = () => {
      setIsSpeaking(true);
      setSpeakingIndex(index);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setSpeakingIndex(null);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setSpeakingIndex(null);
    };

    speechSynthesis.speak(utterance);
  };

  const sendMessage = async () => {
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage("");
    
    const newHistory = [...history, { role: 'user', content: userMessage }];
    setHistory(newHistory);
    
    setLoading(true);
    try {
      // Direct AI chat with real-time read access to the app's database (clients, sales,
      // Rappel, visits) and PDF catalogs/price tables - see /chatbot/ask on the backend.
      const response = await axios.post(`${API}/chatbot/ask`, {
        message: userMessage,
        history: newHistory.slice(-10)
      });
      setHistory(prev => [...prev, { role: 'assistant', content: response.data.answer }]);
    } catch (error) {
      console.error("Chatbot error:", error);
      toast.error("Erro: " + getApiErrorMessage(error));
      setHistory(prev => [...prev, { 
        role: 'assistant', 
        content: "Desculpe, ocorreu um erro. Por favor, tente novamente." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const analyzeWithAI = async (index, query) => {
    setAnalyzingIndex(index);
    try {
      const response = await axios.post(`${API}/chatbot/ask`, {
        message: query,
        history: history.slice(-10)
      });
      setHistory(prev => [...prev, { role: 'assistant', content: response.data.answer }]);
    } catch (error) {
      console.error("Chatbot AI error:", error);
      toast.error("Erro: " + getApiErrorMessage(error));
      setHistory(prev => [...prev, { 
        role: 'assistant', 
        content: "Desculpe, ocorreu um erro ao analisar com IA. Por favor, tente novamente." 
      }]);
    } finally {
      setAnalyzingIndex(null);
    }
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('gresart_chatbot_history');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div 
      className={`bottom-24 md:bottom-4 right-4 transition-all duration-300 ${
        isMinimized ? 'w-72' : isExpanded ? 'w-[92vw] sm:w-[560px] md:w-[680px]' : 'w-80 sm:w-96'
      }`}
      style={{
        position: 'fixed',
        zIndex: 2147483647,
        maxHeight: isMinimized ? '52px' : isExpanded ? 'calc(100vh - 80px)' : 'calc(100vh - 160px)'
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden flex flex-col" 
           style={{ maxHeight: isMinimized ? '52px' : isExpanded ? 'calc(100vh - 80px)' : '500px' }}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[#009392] to-[#00b3b0] px-4 py-3.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-white min-w-0">
            <img src="/assistant-icon.png" alt="Assistente IA" className="w-5 h-5 rounded-full flex-shrink-0 bg-white" data-testid="chatbot-avatar-header" />
            <span className="font-semibold text-sm truncate">Assistente IA Gresart</span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {!isMinimized && (
              <button
                onClick={clearHistory}
                className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
                data-testid="chatbot-clear-history-btn"
                aria-label="Limpar chat"
                title="Limpar chat"
              >
                <Trash size={16} />
              </button>
            )}
            {!isMinimized && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
                data-testid="chatbot-expand-btn"
                aria-label={isExpanded ? "Restaurar tamanho" : "Expandir janela"}
                title={isExpanded ? "Restaurar tamanho" : "Expandir janela"}
              >
                {isExpanded ? <ArrowsIn size={16} /> : <ArrowsOut size={16} />}
              </button>
            )}
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
              data-testid="chatbot-minimize-btn"
            >
              <Minus size={16} />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
              data-testid="chatbot-close-btn"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div
              className="flex-1 overflow-y-auto p-3 space-y-3 bg-stone-50"
              style={{ minHeight: isExpanded ? '400px' : '200px', maxHeight: isExpanded ? 'calc(100vh - 220px)' : '350px' }}
            >
              {history.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-4">
                  <img src="/mascot-full-body.png?v=13" alt="Assistente IA" className="w-[107px] h-auto mb-2" data-testid="chatbot-avatar-empty" />
                  <p className="text-sm text-stone-500">
                    Pergunte sobre clientes, vendas, Rappel, visitas ou produtos do catálogo
                  </p>
                </div>
              ) : (
                <>
                  {history.map((msg, index) => (
                    <div 
                      key={index} 
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div 
                        className={`flex items-start gap-1.5 max-w-[85%] ${
                          msg.role === 'user' ? 'flex-row-reverse' : ''
                        }`}
                      >
                        <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center overflow-hidden ${
                          msg.role === 'user' 
                            ? 'bg-[#009392]' 
                            : 'bg-white border border-stone-200'
                        }`}>
                          {msg.role === 'user' ? (
                            <User size={12} className="text-white" />
                          ) : (
                            <img src="/assistant-icon.png" alt="Assistente IA" className="w-full h-full object-cover" data-testid={`chatbot-avatar-message-${index}`} />
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <div 
                            className={`rounded-xl px-3 py-2 ${
                              msg.role === 'user' 
                                ? 'bg-[#009392] text-white rounded-tr-sm' 
                                : 'bg-white border border-stone-200 rounded-tl-sm shadow-sm'
                            }`}
                          >
                            {msg.kind === 'local_search' ? (
                              <div className="space-y-2" data-testid={`local-search-result-${index}`}>
                                <p className="text-xs font-medium text-stone-700">{msg.summary}</p>
                                {msg.results && msg.results.length > 0 && (
                                  <div className="space-y-2 max-h-56 overflow-y-auto">
                                    {msg.results.map((doc, di) => (
                                      <div key={di} className="bg-stone-50 border border-stone-200 rounded-lg p-2">
                                        <p className="font-semibold text-[#009392] text-[11px] mb-1 truncate">{doc.pdf_name}</p>
                                        {doc.matches.map((m, mi) => (
                                          <p key={mi} className="text-[11px] text-stone-600 leading-snug break-words">
                                            {m.page && <span className="text-stone-400">[Pág. {m.page}] </span>}
                                            {m.line}
                                          </p>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {msg.total_matches === 0 && msg.approximate_match && (
                                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid={`approximate-match-${index}`}>
                                    <p className="text-[11px] text-amber-700 font-medium mb-1">Candidato mais parecido (aproximado):</p>
                                    <p className="font-semibold text-[#009392] text-[11px] mb-0.5 truncate">{msg.approximate_match.pdf_name}</p>
                                    <p className="text-[11px] text-stone-600 leading-snug break-words">
                                      {msg.approximate_match.page && <span className="text-stone-400">[Pág. {msg.approximate_match.page}] </span>}
                                      {msg.approximate_match.line}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className={`text-xs whitespace-pre-wrap ${
                                msg.role === 'user' ? 'text-white' : 'text-stone-700'
                              }`}>
                                {msg.content}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {msg.role === 'assistant' && msg.kind === 'local_search' && (
                              <button
                                onClick={() => analyzeWithAI(index, msg.query)}
                                disabled={analyzingIndex === index}
                                className="self-start flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-[#009392]/10 text-[#009392] hover:bg-[#009392]/20 transition-colors disabled:opacity-60"
                                data-testid={`analyze-ai-btn-${index}`}
                              >
                                {analyzingIndex === index ? (
                                  <>
                                    <LoadingSpinner size={12} />
                                    <span>A analisar...</span>
                                  </>
                                ) : (
                                  <>
                                    <Sparkle size={10} />
                                    <span>Analisar com IA?</span>
                                  </>
                                )}
                              </button>
                            )}
                            {msg.role === 'assistant' && msg.kind !== 'local_search' && speechSynthesis && (
                              <button
                                onClick={() => speakText(msg.content, index)}
                                className={`self-start flex items-center gap-1 text-xs px-2 py-1 rounded-lg transition-colors ${
                                  isSpeaking && speakingIndex === index
                                    ? 'bg-red-100 text-red-600 hover:bg-red-200'
                                    : 'bg-stone-100 text-stone-500 hover:bg-stone-200'
                                }`}
                                data-testid={`speak-message-${index}`}
                              >
                                {isSpeaking && speakingIndex === index ? (
                                  <>
                                    <Stop size={10} weight="fill" />
                                    <span>Parar</span>
                                  </>
                                ) : (
                                  <>
                                    <SpeakerHigh size={10} />
                                    <span>Ouvir</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {loading && (
                    <div className="flex justify-start">
                      <div className="flex items-center gap-1.5">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center bg-white border border-stone-200 overflow-hidden">
                          <img src="/assistant-icon.png" alt="Assistente IA" className="w-full h-full object-cover" />
                        </div>
                        <div className="bg-white border border-stone-200 rounded-xl rounded-tl-sm px-3 py-2 shadow-sm">
                          <div className="flex items-center gap-1.5">
                            <LoadingSpinner size={16} />
                            <span className="text-xs text-stone-500">A consultar os dados...</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-stone-200 bg-white flex-shrink-0">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={isListening ? "A ouvir..." : "Escreva a sua pergunta..."}
                  className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009392]/50 ${
                    isListening ? 'border-red-400 bg-red-50' : 'border-stone-200'
                  }`}
                  disabled={loading}
                  data-testid="chatbot-input"
                />
                {SpeechRecognition && (
                  <Button
                    onClick={toggleListening}
                    disabled={loading}
                    size="sm"
                    variant={isListening ? "destructive" : "outline"}
                    className={`px-3 ${isListening ? 'animate-pulse' : ''}`}
                    data-testid="chatbot-voice-btn"
                  >
                    <Microphone size={16} weight={isListening ? "fill" : "regular"} />
                  </Button>
                )}
                <Button
                  onClick={sendMessage}
                  disabled={loading || !message.trim()}
                  size="sm"
                  className="bg-[#009392] hover:bg-[#007a7a] px-3"
                  data-testid="chatbot-send-btn"
                >
                  {loading ? (
                    <LoadingSpinner size={16} />
                  ) : (
                    <PaperPlaneRight size={16} />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
